/**
 * Invoices Table - Lists invoices with status chips and approval indicator
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Plus, FileText, Clock, CheckCircle2, XCircle, Send } from 'lucide-react';
import { formatCurrency, formatDate, INVOICE_STATUS_CONFIG, APPROVAL_STATUS_CONFIG } from './utils';

// Status chip component
const StatusChip = ({ status, config }) => {
  const statusInfo = config[status] || { label: status, color: 'bg-slate-100 text-slate-600' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
      {statusInfo.label}
    </span>
  );
};

// Approval indicator
const ApprovalIndicator = ({ approvalStatus }) => {
  if (!approvalStatus || approvalStatus === 'not_submitted') return null;
  
  const icons = {
    pending: <Clock className="h-3.5 w-3.5 text-amber-500" />,
    approved: <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />,
    rejected: <XCircle className="h-3.5 w-3.5 text-red-500" />
  };
  
  return (
    <span className="ml-2" title={`Approval: ${approvalStatus}`}>
      {icons[approvalStatus]}
    </span>
  );
};

export const InvoicesTable = ({ 
  invoices = [], 
  canEdit, 
  onCreateInvoice,
  onPostInvoice,
  onViewInvoice
}) => {
  return (
    <Card data-testid="invoices-table">
      <CardHeader className="flex flex-row items-center justify-between py-4">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <FileText className="h-4 w-4 text-blue-500" />
          Invoices
        </CardTitle>
        {canEdit && (
          <Button 
            size="sm" 
            onClick={onCreateInvoice}
            data-testid="create-invoice-btn"
          >
            <Plus className="h-4 w-4 mr-1" />
            Create Invoice
          </Button>
        )}
      </CardHeader>
      <CardContent className="pt-0">
        {invoices.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Due Date</TableHead>
                  {canEdit && <TableHead className="w-[100px]"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map(inv => (
                  <TableRow 
                    key={inv.id}
                    className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    onClick={() => onViewInvoice?.(inv)}
                    data-testid={`invoice-row-${inv.id}`}
                  >
                    <TableCell className="font-medium">
                      {inv.transaction_number || inv.invoice_number}
                      <ApprovalIndicator approvalStatus={inv.approval_status} />
                    </TableCell>
                    <TableCell className="text-slate-500">
                      {formatDate(inv.transaction_date || inv.created_at)}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(inv.total_amount || inv.amount)}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={inv.balance_due > 0 ? 'text-amber-600' : 'text-green-600'}>
                        {formatCurrency(inv.balance_due)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={inv.status} config={INVOICE_STATUS_CONFIG} />
                    </TableCell>
                    <TableCell className="text-slate-500">
                      {formatDate(inv.due_date)}
                    </TableCell>
                    {canEdit && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {inv.status === 'draft' && inv.approval_status === 'approved' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onPostInvoice?.(inv)}
                            data-testid={`post-invoice-${inv.id}`}
                          >
                            <Send className="h-3 w-3 mr-1" />
                            Post
                          </Button>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-8">
            <FileText className="h-10 w-10 mx-auto text-slate-300 mb-2" />
            <p className="text-slate-500">No invoices yet</p>
            {canEdit && (
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-3"
                onClick={onCreateInvoice}
              >
                <Plus className="h-4 w-4 mr-1" />
                Create First Invoice
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default InvoicesTable;
